/**
 * useAttachmentUpload — per-file sign → S3 POST → poll-to-clean lifecycle.
 *
 * Flow per file:
 *   1. Client-side type + size validation (immediate, no network).
 *   2. POST /uploads/sign via apiClient (JWT + Idempotency-Key auto-attached).
 *   3. Plain fetch() POST to the presigned S3 URL (no auth — goes directly to S3).
 *   4. Poll GET /uploads/{id}/status until scan_status is clean/infected/failed
 *      or the poll timeout elapses.
 *
 * State transitions: uploading → scanning → ready | error.
 */
import { useCallback, useRef, useState } from "react";

import { ensureGuestSession } from "@/features/chat/guest/api";
import { isRegistrationRequired } from "@/features/chat/guest/allowance";
import { apiClient } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import {
  type AttachmentDraft,
  kindForType,
  maxBytesFor,
} from "@/features/chat/attachment-types";

/** Sign endpoint response (mirrors backend SignResponse). */
interface SignResponse {
  upload_id: string;
  presigned_url: string;
  presigned_fields: Record<string, string>;
  s3_key?: string;
  expires_at?: string;
  max_size_bytes?: number;
}

/** Status poll response (mirrors backend UploadStatusResponse). */
interface StatusResponse {
  upload_id: string;
  scan_status: "pending" | "clean" | "infected" | "failed";
  s3_key_clean?: string | null;
}

interface SignBody {
  purpose: string;
  content_type: string;
  content_length: number;
}

const POLL_TIMEOUT_MS = 30_000;
const DEFAULT_POLL_INTERVAL_MS = 1_000;

export interface UseAttachmentUploadOptions {
  /** Override poll interval in ms — primarily for tests. */
  pollIntervalMs?: number;
  /**
   * Guest chat with attachments switched on (`guest.attachments_enabled`):
   * the first upload creates the guest session; sign + status carry the guest
   * token. Absent/false for members — unchanged.
   */
  guest?: boolean;
  /**
   * Guest only: the server refused with 403 `auth.registration_required` —
   * attachments were switched off after the config was read (amendment B-7).
   * The draft is dropped and the caller opens the sign-up gate.
   */
  onSignInRequired?: () => void;
}

export interface UseAttachmentUploadResult {
  drafts: AttachmentDraft[];
  /** Add a file to the pipeline; resolves once initial state is set. */
  add: (file: File) => Promise<AttachmentDraft>;
  /** Remove a draft by localId; revokes any object URL. */
  remove: (localId: string) => void;
  /**
   * Remove all drafts WITHOUT revoking their object URLs. Called after a
   * successful send, where `ready` drafts' blob URLs have just been handed
   * off to the optimistic message bubble (`draftToDisplay` in
   * `MessageInput.tsx`) and are still in use there — revoking them here would
   * immediately break that just-sent thumbnail. Un-revoked blob URLs are
   * scoped to the page lifetime and are reclaimed on navigation/reload.
   */
  clear: () => void;
  /** Upload IDs for drafts that have reached the `ready` state. */
  readyUploadIds: string[];
}

export function useAttachmentUpload(
  options: UseAttachmentUploadOptions = {},
): UseAttachmentUploadResult {
  const pollMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const guest = options.guest === true;
  const onSignInRequiredRef = useRef(options.onSignInRequired);
  onSignInRequiredRef.current = options.onSignInRequired;
  const [drafts, setDrafts] = useState<AttachmentDraft[]>([]);
  const counterRef = useRef(0);

  /** Immutable patch for a single draft (by localId). */
  const patch = useCallback(
    (localId: string, next: Partial<AttachmentDraft>): void => {
      setDrafts((cur) =>
        cur.map((d) => (d.localId === localId ? { ...d, ...next } : d)),
      );
    },
    [],
  );

  const safeRevoke = (dataUrl: string | null): void => {
    if (
      dataUrl !== null &&
      typeof URL !== "undefined" &&
      typeof URL.revokeObjectURL === "function"
    ) {
      URL.revokeObjectURL(dataUrl);
    }
  };

  const add = useCallback(
    async (file: File): Promise<AttachmentDraft> => {
      const localId = `att-${counterRef.current++}`;
      const kind = kindForType(file.type);

      const base: AttachmentDraft = {
        localId,
        file,
        kind: kind ?? "image",
        state: "uploading",
        progress: 0,
        uploadId: null,
        thumbDataUrl:
          kind === "image" &&
          typeof URL !== "undefined" &&
          typeof URL.createObjectURL === "function"
            ? URL.createObjectURL(file)
            : null,
        errorCode: null,
      };

      // --- Client-side validation (no network) ---
      if (kind === null) {
        const errored: AttachmentDraft = {
          ...base,
          state: "error",
          errorCode: "unsupported_type",
        };
        setDrafts((cur) => [...cur, errored]);
        return errored;
      }
      if (file.size > maxBytesFor(file.type)) {
        const errored: AttachmentDraft = {
          ...base,
          state: "error",
          errorCode: "too_large",
        };
        setDrafts((cur) => [...cur, errored]);
        return errored;
      }

      setDrafts((cur) => [...cur, base]);

      // --- Async upload pipeline (runs after initial state is set) ---
      void (async () => {
        try {
          if (guest && useAuthStore.getState().accessToken === null) {
            await ensureGuestSession();
          }

          // 1. Sign the upload with the API (JWT + Idempotency-Key auto-attached).
          const sign = await apiClient.post<SignResponse, SignBody>(
            "/uploads/sign",
            {
              purpose: "chat-document",
              content_type: file.type,
              content_length: file.size,
            },
            guest ? { allowGuest: true } : {},
          );

          // 2. POST the file to S3 using the presigned form fields.
          const form = new FormData();
          for (const [key, value] of Object.entries(sign.presigned_fields)) {
            form.append(key, value);
          }
          // The file must be appended last (S3 presigned-POST requirement).
          form.append("file", file);

          const s3Resp = await fetch(sign.presigned_url, {
            method: "POST",
            body: form,
          });
          if (!s3Resp.ok) {
            throw new Error("s3_upload_failed");
          }

          patch(localId, {
            state: "scanning",
            progress: 1,
            uploadId: sign.upload_id,
          });

          // 3. Poll until the ClamAV scan completes (or timeout).
          const deadline = Date.now() + POLL_TIMEOUT_MS;
          while (true) {
            const status = await apiClient.get<StatusResponse>(
              `/uploads/${sign.upload_id}/status`,
              guest ? { allowGuest: true } : {},
            );

            if (status.scan_status === "clean") {
              patch(localId, { state: "ready" });
              break;
            }
            if (
              status.scan_status === "infected" ||
              status.scan_status === "failed"
            ) {
              patch(localId, { state: "error", errorCode: status.scan_status });
              break;
            }
            if (Date.now() > deadline) {
              patch(localId, { state: "error", errorCode: "scan_timeout" });
              break;
            }

            await new Promise<void>((resolve) =>
              setTimeout(resolve, pollMs),
            );
          }
        } catch (err) {
          // B-7: attachments switched off for guests server-side — a sign-in
          // requirement, not a failed upload. Drop the draft (and its
          // preview URL) and let the caller open the sign-up gate.
          if (guest && isRegistrationRequired(err)) {
            safeRevoke(base.thumbDataUrl);
            setDrafts((cur) => cur.filter((d) => d.localId !== localId));
            onSignInRequiredRef.current?.();
            return;
          }
          patch(localId, { state: "error", errorCode: "upload_failed" });
        }
      })();

      return base;
    },
    [patch, pollMs, guest],
  );

  const remove = useCallback((localId: string): void => {
    setDrafts((cur) => {
      const target = cur.find((d) => d.localId === localId);
      safeRevoke(target?.thumbDataUrl ?? null);
      return cur.filter((d) => d.localId !== localId);
    });
  }, []);

  // Intentionally does NOT revoke object URLs — see the `clear` doc comment
  // on `UseAttachmentUploadResult` for why (blob URLs of just-sent `ready`
  // drafts are still referenced by the optimistic message bubble).
  const clear = useCallback((): void => {
    setDrafts([]);
  }, []);

  const readyUploadIds: string[] = drafts
    .filter((d) => d.state === "ready" && d.uploadId !== null)
    .map((d) => d.uploadId as string);

  return { drafts, add, remove, clear, readyUploadIds };
}
